import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import ProductCard from '@/components/product/ProductCard';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { productsAPI, brandsAPI } from '@/utils/api';

const Catalog = ({ species }) => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = {
    species,
    brand: searchParams.get('brand'),
    life_stage: searchParams.get('life_stage'),
    format: searchParams.get('format'),
    is_grain_free: searchParams.get('is_grain_free') === 'true' ? true : undefined,
    is_holistic: searchParams.get('is_holistic') === 'true' ? true : undefined,
    is_specialized: searchParams.get('is_specialized') === 'true' ? true : undefined,
    protein: searchParams.get('protein')
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, b] = await Promise.all([
          productsAPI.getAll(filters),
          brandsAPI.getAll()
        ]);
        setProducts(p);
        setBrands(b.filter(br => br.species?.includes(species)));
      } finally {
        setLoading(false);
      }
    })();
  }, [searchParams, species]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null || value === undefined || value === '' || value === false) {
      newParams.delete(key);
    } else {
      newParams.set(key, value.toString());
    }
    setSearchParams(newParams);
  };

  const clearAll = () => setSearchParams({});

  const activeFilters = Object.entries(filters).filter(([k, v]) => k !== 'species' && v);

  const FilterPanel = () => (
    <div className="space-y-6">
      <div>
        <p className="font-display font-semibold text-sm uppercase tracking-widest text-[#1C1917] mb-3">Marca</p>
        <div className="space-y-2">
          {brands.map((b) => (
            <div key={b.id} className="flex items-center gap-2">
              <Checkbox
                id={`brand-${b.slug}`}
                checked={filters.brand === b.slug}
                onCheckedChange={(checked) => updateFilter('brand', checked ? b.slug : null)}
                data-testid={`filter-brand-${b.slug}`}
              />
              <Label htmlFor={`brand-${b.slug}`} className="text-sm cursor-pointer">{b.name}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="font-display font-semibold text-sm uppercase tracking-widest text-[#1C1917] mb-3">Etapa de vida</p>
        <div className="space-y-2">
          {[
            { v: 'cachorro', l: species === 'gato' ? 'Gatito' : 'Cachorro' },
            { v: 'adulto', l: 'Adulto' },
            { v: 'senior', l: 'Senior' }
          ].map((s) => (
            <div key={s.v} className="flex items-center gap-2">
              <Checkbox
                id={`stage-${s.v}`}
                checked={filters.life_stage === s.v}
                onCheckedChange={(checked) => updateFilter('life_stage', checked ? s.v : null)}
                data-testid={`filter-life-stage-${s.v}`}
              />
              <Label htmlFor={`stage-${s.v}`} className="text-sm cursor-pointer">{s.l}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="font-display font-semibold text-sm uppercase tracking-widest text-[#1C1917] mb-3">Formato</p>
        <div className="space-y-2">
          {[
            { v: 'seco', l: 'Alimento seco' },
            { v: 'húmedo', l: 'Alimento húmedo' }
          ].map((f) => (
            <div key={f.v} className="flex items-center gap-2">
              <Checkbox
                id={`fmt-${f.v}`}
                checked={filters.format === f.v}
                onCheckedChange={(checked) => updateFilter('format', checked ? f.v : null)}
                data-testid={`filter-format-${f.v}`}
              />
              <Label htmlFor={`fmt-${f.v}`} className="text-sm cursor-pointer">{f.l}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="font-display font-semibold text-sm uppercase tracking-widest text-[#1C1917] mb-3">Características</p>
        <div className="space-y-2">
          {[
            { v: 'is_grain_free', l: 'Libre de granos' },
            { v: 'is_holistic', l: 'Holístico' },
            { v: 'is_specialized', l: 'Nutrición especializada' }
          ].map((c) => (
            <div key={c.v} className="flex items-center gap-2">
              <Checkbox
                id={`char-${c.v}`}
                checked={filters[c.v] === true}
                onCheckedChange={(checked) => updateFilter(c.v, checked ? true : null)}
                data-testid={`filter-${c.v.replace(/_/g, '-')}`}
              />
              <Label htmlFor={`char-${c.v}`} className="text-sm cursor-pointer">{c.l}</Label>
            </div>
          ))}
        </div>
      </div>

      {activeFilters.length > 0 && (
        <Button
          onClick={clearAll}
          variant="outline"
          className="w-full border-[#D47A4A] text-[#D47A4A] hover:bg-[#D47A4A] hover:text-white rounded-full"
          data-testid="filter-clear-all"
        >
          Limpiar filtros
        </Button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
      {/* Header */}
      <div className="mb-8">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Catálogo</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight">
          Alimentos para {species === 'perro' ? 'perros' : 'gatos'}
        </h1>
        <p className="text-[#57534E] mt-2">
          {loading ? 'Cargando...' : `${products.length} productos encontrados`}
        </p>
      </div>

      <div className="flex gap-8">
        {/* Filters (desktop) */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-28">
            <FilterPanel />
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1">
          {/* Mobile filter toggle */}
          <div className="lg:hidden mb-4 flex items-center justify-between">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="border-[#2A5C43] text-[#2A5C43] rounded-full"
              data-testid="catalog-mobile-filter-toggle"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filtros {activeFilters.length > 0 && `(${activeFilters.length})`}
            </Button>
          </div>

          {showFilters && (
            <div className="lg:hidden bg-white rounded-2xl p-6 mb-6 border border-[#E7E5E4]">
              <FilterPanel />
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-[#F7F2E8] rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-[#E7E5E4]">
              <p className="text-lg font-display text-[#1C1917] mb-2">No se encontraron productos</p>
              <p className="text-sm text-[#57534E] mb-4">Prueba ajustar los filtros</p>
              <Button onClick={clearAll} className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full" data-testid="catalog-no-results-clear">
                Limpiar filtros
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Catalog;
